<!-- The guided way in: scan, then look at what came back, then design. THIS FILE IS YOURS.

     WHY THIS EXISTS. The scan worked and the flow around it did not. A person tapped "Scan
     the room", walked their kitchen, and got a card halfway down the Room sheet with a
     dozen lines of notes, a services checklist, two buttons of equal weight ("New design
     from this room" / "Start from the existing layout"), two more grey links under those,
     and — below all of it — the shape presets and the wall fields the scan had just made
     irrelevant. Then the design came out on a different tab, and the finishes on a third,
     and the review on a fourth. The owner's words after using it on his own phone: "the
     flow for the user through the app to scan and then design is a bit confusing and the
     buttons are not clear."

     So this is one card at a time, in the Room sheet, each with ONE obvious primary button
     and at most one quiet alternative. Nothing here decides anything: PlannerPage owns
     every piece of state and every consequence, and this file only ever says which stage it
     is on and shouts when a finger lands. That is deliberate — the stages are a state
     machine and a state machine split across two files is a state machine nobody can read.

     Five stages:
       start    the hero. Scan, or type the walls in.
       scanned  what came back, and the three things you can do about it.
       walls    which of the room's walls the kitchen is allowed on. Only after a scan,
                and the plan behind this sheet is where the answer is actually given.
       problem  a scan came back and could not be made into a room. See below: this is a
                CARD because it used to be a pill that faded in three seconds.
       photos   the one thing LiDAR cannot see, offered per wall. Only where the server can
                read a picture (features.readImage); otherwise this stage never happens. -->
<template>
  <div class="flow">
    <!-- ── 1. START ────────────────────────────────────────────────────────────────
         The hero, and nothing beside it. Measuring the room is the first job and on the
         right phone the fastest way to do it is to walk around pointing the camera; the
         wall fields are the ALTERNATIVE, so they are behind a quiet line rather than
         standing under the scan competing with it. -->
    <section v-if="stage === 'start'" class="step start">
      <template v-if="canScan">
        <button class="hero" type="button" :disabled="busy" @click="$emit('scan')">
          <span class="glyph">
            <svg width="28" height="28" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7V4.5A1.5 1.5 0 0 1 4.5 3H7"/><path d="M13 3h2.5A1.5 1.5 0 0 1 17 4.5V7"/><path d="M17 13v2.5a1.5 1.5 0 0 1-1.5 1.5H13"/><path d="M7 17H4.5A1.5 1.5 0 0 1 3 15.5V13"/><circle cx="10" cy="10" r="2.6"/></svg>
          </span>
          <span class="text">
            <span class="k">{{ busy ? 'Scanning…' : 'Scan your kitchen' }}</span>
            <!-- THE ONE SENTENCE THAT DECIDES WHETHER A SCAN WORKS, on the button that
                 starts one. It is in ScanIntro too, at more length -- but ScanIntro is shown
                 ONCE and then never again, and this is what somebody reads on the day they
                 have forgotten it. Walking into the next room and stopping half way round
                 are the two ways a scan comes back unusable. -->
            <span class="b">Walk the kitchen only, all the way round and back to where you
              started, pointing the phone at the walls. About a minute.</span>
          </span>
          <svg class="go" width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 4l6 6-6 6"/></svg>
        </button>
        <button class="quiet" type="button" @click="$emit('type-walls')">
          or type the walls in instead
        </button>
      </template>

      <!-- No LiDAR: one line saying which of the reasons it is, and the wall fields are
           already open below. A feature that vanishes on the devices that cannot run it
           reads as unbuilt, which is exactly how this looked on the web. -->
      <p v-else class="noscan">
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7V4.5A1.5 1.5 0 0 1 4.5 3H7"/><path d="M13 3h2.5A1.5 1.5 0 0 1 17 4.5V7"/><path d="M17 13v2.5a1.5 1.5 0 0 1-1.5 1.5H13"/><path d="M7 17H4.5A1.5 1.5 0 0 1 3 15.5V13"/><circle cx="10" cy="10" r="2.6"/></svg>
        <!-- `scanWhy` is a whole sentence and says which of the reasons it is. Nothing is
             put in front of it: two sentences saying the same thing is how a one-line
             apology becomes a paragraph nobody finishes. -->
        <span>{{ scanWhy }} <b>Type the walls in below</b> and it designs from those.</span>
      </p>
    </section>

    <!-- ── 2. SCANNED ──────────────────────────────────────────────────────────────
         ONE SENTENCE, then the decision. The full notes are still here and still complete
         — they are the only account anybody has of a scan that went wrong — but they are
         behind a disclosure, because eleven lines of diagnostics between a person and the
         button they want is eleven lines they will not read either. -->
    <section v-else-if="stage === 'scanned'" class="step found">
      <p class="k">{{ found?.runs?.length ? 'Found your kitchen' : 'What the scan found' }}</p>
      <p class="saw">{{ foundLine }}</p>

      <details class="more">
        <summary>See the details</summary>
        <p v-if="sawLine" class="b">{{ sawLine }}</p>
        <!-- Walls the loop set aside. Not a fault and not shown as one: a person scanning a
             kitchen walks through the doorway, and the room that comes back is smaller than
             the phone was pointed at. It is said here rather than nowhere. -->
        <p v-if="leftOverLine" class="b">{{ leftOverLine }}</p>
        <p v-for="(n, i) in found?.notes ?? []" :key="i" class="b">{{ n }}</p>
      </details>

      <!-- What is plumbed or gassed in, offered as a choice rather than decided for them:
           keeping it in place is right when the pipe stays where it is, and wrong the one
           time in ten a person is redoing the plumbing too. -->
      <div v-if="found?.services?.length" class="services">
        <p class="b lock-lede">
          The scan found {{ serviceLine }}. Moving one means moving its plumbing or
          its gas point — keep any of these where they are?
        </p>
        <div class="ticks">
          <label v-for="sv in found.services" :key="sv.id" class="lockrow">
            <input type="checkbox" :checked="!!locks[sv.id]"
                   @change="$emit('toggle-lock', sv.id, $event.target.checked)" />
            <span>{{ serviceWord[sv.role] ?? sv.role }}</span>
          </label>
        </div>
      </div>

      <!-- The island's FOOTPRINT, which is a fact about the room in a way the cupboards on
           it are not: it is where the family walks around, and where the slab and the power
           under the floor already are. Offered, and ticked, because keeping it is nearly
           always right. -->
      <label v-if="hasIsland" class="lockrow island">
        <input type="checkbox" :checked="keepIsland"
               @change="$emit('update:keepIsland', $event.target.checked)" />
        <span>Keep the island where it is</span>
      </label>

      <!-- The one that matters, in the words of what it does. Neither of these edits the
           scan: the room was written to storage the moment it came back, and both of these
           make a DESIGN from it. -->
      <button type="button" class="primary" @click="$emit('design')">
        Design me a new kitchen
      </button>
      <button v-if="found?.runs?.length" type="button" class="secondary"
              @click="$emit('keep-layout')">
        Keep what's there and change it
      </button>

      <!-- Small, last, and still all here: looking at the room, and the report that is the
           only way a scan that went wrong can be read from this end. -->
      <div class="tertiary">
        <button v-if="canLook" type="button" @click="$emit('look')">Look at the scan</button>
        <button type="button" @click="$emit('copy')">Copy the scan report</button>
        <button v-if="hasScanFile" type="button" @click="$emit('share')">Share the 3D file</button>
      </div>
    </section>

    <!-- ── 3. WHICH WALLS ARE THE KITCHEN ──────────────────────────────────────────
         THE OWNER'S OWN ANSWER TO THE OPEN-PLAN SCAN.

         His scan came back with the whole floor -- twenty walls, kitchen and dining and
         lounge, one closed loop -- and every wall of it is real, so the design came out
         spread across the lot with cupboards along a living-room wall and a vast empty
         middle. His words: "Add a step where you can drag / draw / define what walls you'd
         like to be available for kitchen after the initial scan." No heuristic beats being
         asked, and the room is already on screen behind this card in plan.

         So the card is deliberately almost nothing: the plan does the talking, this says how
         much kitchen the ticks add up to and offers the one button. What comes up ticked is
         `kitchenWalls` in reproduce.js -- a benchtop, then an appliance, then a cupboard-
         shaped box that is not a television unit -- and on the owner's scan that is his five
         walls and his island, with the fifteen lounge and dining walls left out. -->
    <section v-else-if="stage === 'walls'" class="step found">
      <p class="k">Which walls are the kitchen?</p>
      <p class="b">
        Tap a wall on the plan to put cabinets on it or take them off. The ones the scan
        found a kitchen against are already on.
      </p>
      <!-- The running total, which is the thing worth reading before pressing the button:
           a person knows their own kitchen in metres of bench, not in walls. -->
      <p class="saw tally">{{ pickLine }}</p>

      <div class="tertiary picks">
        <button type="button" @click="$emit('pick-all')">Select all</button>
        <button type="button" @click="$emit('pick-none')">Clear</button>
      </div>

      <button type="button" class="primary" @click="$emit('design')">
        Design me a new kitchen
      </button>
      <button type="button" class="secondary" @click="$emit('back')">
        Back to what was found
      </button>
    </section>

    <!-- ── 4. PROBLEM ──────────────────────────────────────────────────────────────
         A SCAN THAT CAME BACK IS NEVER LOST AND NEVER DEAD-ENDS.

         The owner scanned his kitchen, RoomPlan drew its boxes round the cupboards and the
         sink, he pressed Done — and got the Start card back with the demo kitchen behind
         it. "It scanned but then wouldn't let me progress." What had happened was a pill
         with an exception in it, faded before the phone was back up.

         So: which of the three it was, in a sentence a person can act on; the machine's own
         words underneath rather than instead; and four ways forward, none of which throws
         the scan away. Scanning again is the one that usually works, so it carries the one
         piece of advice that decides whether the next one does. -->
    <section v-else-if="stage === 'problem'" class="step problem">
      <p class="k">{{ problemKind === 'error' ? 'The scan did not come through'
        : 'These walls do not make a room' }}</p>
      <p class="saw">
        <template v-if="problemKind === 'error'">
          The scanner stopped before it could hand the room over. Nothing is wrong with your
          kitchen — this one is ours.
        </template>
        <template v-else>
          The walls came back, but they do not join up into a room the kitchen can be laid
          out in. Everything the scan measured is still here.
        </template>
      </p>
      <!-- The exception, or the loop's own sentence. Never the whole message and never the
           heading: "TypeError: cannot read length of undefined" is not something anybody
           can do anything about, and it was the entire text a person used to be given. -->
      <p v-if="problemDetail" class="detail">{{ problemDetail }}</p>

      <button type="button" class="primary" :disabled="busy" @click="$emit('scan')">
        {{ busy ? 'Scanning…' : 'Scan again' }}
      </button>
      <p class="advice">
        Walk the kitchen and only the kitchen — not through into the next room — and go all
        the way round, back to where you started.
      </p>

      <button v-if="!typed" type="button" class="secondary" @click="$emit('type-walls')">
        Type the walls in instead
      </button>

      <!-- Always both, and the report especially: it is the only account of this scan that
           exists, it works when no room was built, and it is what turns "it wouldn't let me
           progress" into a fixable bug. -->
      <div class="tertiary">
        <button type="button" @click="$emit('copy')">Copy the scan report</button>
        <button v-if="hasRawFile" type="button" @click="$emit('open-scan-file')">
          View the 3D scan file
        </button>
      </div>
    </section>

    <!-- ── 5. PHOTOS ───────────────────────────────────────────────────────────────
         Optional, and said so. A door separation is a 3mm reveal and an iPhone's depth
         camera resolves at 10 to 20mm, so a run of four doors comes back as one flat
         plane; the divisions are in the CAMERA image, not the depth one. Skipping is a
         real answer — the app has already guessed, and the guess is a normal kitchen. -->
    <section v-else class="step found">
      <p class="k">Photograph each wall <span class="opt">optional</span></p>
      <p class="b">
        The scan measured these walls but cannot see where the doors and drawers divide —
        that is a 3mm gap, and LiDAR reads it as a flat front. One photo settles a wall.
      </p>
      <button v-for="w in photoWalls" :key="w.wallId" type="button" class="wallshot"
              :disabled="busy" @click="$emit('photograph', w)">
        <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6.5h3l1.5-2h5l1.5 2h3v9H3Z"/><circle cx="10" cy="11" r="2.8"/></svg>
        <span class="text">
          <span class="k">Photograph {{ w.name.toLowerCase() }}</span>
          <span class="b">{{ w.mm }} mm of cabinets</span>
        </span>
      </button>
      <button type="button" class="primary" @click="$emit('design')">
        {{ busy ? 'Reading…' : 'Design me a new kitchen' }}
      </button>
      <button type="button" class="secondary" @click="$emit('back')">Back to what was found</button>
    </section>
  </div>
</template>

<script setup>
/*
 * Every one of these is a fact PlannerPage already holds, handed down rather than worked
 * out again here. The words that vary by kitchen — what was found, which services, which
 * walls — are computed up there because that is where the plan and the maker's range are,
 * and a second copy of that arithmetic in a presentation component is a second copy to get
 * wrong.
 */
defineProps({
  /** 'start' | 'scanned' | 'walls' | 'problem' | 'photos'. The only thing that decides
      what is shown. */
  stage: { type: String, default: 'start' },
  /**
   * How much kitchen the picked walls add up to, in one sentence -- "5 walls and the island
   * — 8.6 m of cabinets". Computed in PlannerPage, where the plan and the maker's limits
   * are: how long a run on a wall actually comes out is `room.extents`' arithmetic, corners
   * and doorways and all, and a second copy of it in here is a second copy to get wrong.
   */
  pickLine: { type: String, default: '' },
  /** Whether this device can scan at all, and the one-line reason when it cannot. */
  canScan: { type: Boolean, default: false },
  scanWhy: { type: String, default: '' },
  /** A scan or a photo read is running: every button that starts another one goes quiet. */
  busy: { type: Boolean, default: false },
  /** What `fromScan` returned: runs, services, notes, saw. */
  found: { type: Object, default: null },
  /** One sentence of what came back, for somebody who will not read eleven. */
  foundLine: { type: String, default: '' },
  /** The counts, verbatim, behind the disclosure. */
  sawLine: { type: String, default: '' },
  /** Walls that were measured and are not in this room, when there were any. */
  leftOverLine: { type: String, default: '' },
  serviceLine: { type: String, default: '' },
  /** Which plumbed-or-gassed items to keep where they are, keyed by item id. */
  locks: { type: Object, default: () => ({}) },
  /** The maker's word for each service role, so this file holds no vocabulary of its own. */
  serviceWord: { type: Object, default: () => ({}) },
  keepIsland: { type: Boolean, default: true },
  hasIsland: { type: Boolean, default: false },
  /** The walls whose fronts a photograph could divide. Empty unless the server reads images. */
  photoWalls: { type: Array, default: () => [] },
  /** There is a room saved to look at, and a USDZ of it to hand somebody. */
  canLook: { type: Boolean, default: false },
  hasScanFile: { type: Boolean, default: false },
  /*
   * WHICH OF THE THREE WENT WRONG, on the problem stage. A cancel is not one of them: it is
   * not a problem, it gets no card, and this component is never put on that stage for it.
   *   'error'     the scanner itself threw
   *   'unclosed'  walls came back and would not make a room
   * `problemDetail` is the machine's own words -- an exception, or the loop's sentence --
   * shown UNDER the plain one, never as it.
   */
  problemKind: { type: String, default: null },
  problemDetail: { type: String, default: null },
  /** The walls are already being typed in, so the card stops offering it. */
  typed: { type: Boolean, default: false },
  /** THIS scan wrote a 3D file, as against the one some earlier design remembers. */
  hasRawFile: { type: Boolean, default: false },
});

defineEmits([
  'scan', 'type-walls', 'design', 'keep-layout', 'photograph',
  'look', 'copy', 'share', 'open-scan-file', 'toggle-lock', 'update:keepIsland', 'back',
  'pick-all', 'pick-none',
]);
</script>

<style scoped>
.flow { display: block; }
.step { display: block; }

/* ── the hero ─────────────────────────────────────────────────────────────────
   The full width of the panel, the darkest thing on it, and the reason it exists
   written next to it. There is nothing else at this stage to compare it with, which
   is the whole point. */
.hero {
  display: flex; align-items: center; gap: 13px; width: 100%; text-align: left;
  padding: 14px 13px; margin: 2px 0 0; min-height: 76px;
  border: 1px solid var(--k-ink); border-radius: 13px; cursor: pointer;
  background: var(--k-ink); color: var(--k-paper);
}
.hero:active { opacity: .9; }
.hero:disabled { opacity: .6; }
.hero .glyph { flex: 0 0 auto; display: grid; place-items: center; opacity: .9; }
.hero .text { flex: 1; display: grid; gap: 3px; min-width: 0; }
.hero .k { font: 600 15.5px/1.2 var(--ion-font-family, system-ui); }
.hero .b { font: 400 11.5px/1.35 var(--ion-font-family, system-ui); opacity: .72; }
.hero .go { flex: 0 0 auto; opacity: .6; }

/* The alternative, and it reads as one: a line, not a second button. */
.start .quiet {
  display: block; width: 100%; margin: 4px 0 0; padding: 11px 2px; min-height: 40px;
  border: 0; background: none; cursor: pointer; text-align: center;
  font: 500 12.5px/1 var(--ion-font-family, system-ui); color: var(--k-accent);
}

.noscan {
  display: flex; align-items: flex-start; gap: 9px; margin: 2px 0 4px; padding: 10px 11px;
  border: 1px dashed var(--k-line); border-radius: 11px;
  font: 400 11.5px/1.4 var(--ion-font-family, system-ui); color: var(--k-mute);
}
.noscan svg { flex: 0 0 auto; margin-top: 1px; }
.noscan b { font-weight: 600; color: var(--k-dim); }

/* ── what came back ───────────────────────────────────────────────────────────
   Same card the found-kitchen notes always had, at a third of the height: the
   sentence is what a person reads, the disclosure is what a support conversation
   reads. */
.found {
  margin: 2px 0 4px; padding: 12px 13px; border-radius: 12px;
  background: color-mix(in oklab, var(--k-accent) 9%, var(--k-paper));
  border: 1px solid color-mix(in oklab, var(--k-accent) 28%, transparent);
}
.found .k {
  margin: 0 0 5px; font: 600 14px/1.2 var(--ion-font-family, system-ui); color: var(--k-ink);
}
.found .opt {
  margin-left: 6px; padding: 2px 6px; border-radius: 999px; background: var(--k-fill);
  font: 500 10px/1.5 var(--ion-font-family, system-ui);
  letter-spacing: .04em; text-transform: uppercase; color: var(--k-mute);
}
.found .saw {
  margin: 0 0 8px; font: 500 12.5px/1.45 var(--ion-font-family, system-ui); color: var(--k-ink);
}
.found .b { margin: 0 0 5px; font: 400 12px/1.45 var(--ion-font-family, system-ui); color: var(--k-dim); }

/* The notes, whole, one tap away. `details` rather than a ref and a v-if because the
   browser's own disclosure is keyboard- and screen-reader-correct for free. */
.more { margin: 0 0 9px; }
.more summary {
  cursor: pointer; padding: 5px 0; list-style: none;
  font: 500 12px/1 var(--ion-font-family, system-ui); color: var(--k-accent);
}
.more summary::-webkit-details-marker { display: none; }
.more summary::after { content: ' ▸'; }
.more[open] summary::after { content: ' ▾'; }
.more[open] { padding-bottom: 2px; }

/* ── the scan that did not become a room ──────────────────────────────────────
   The same card as the found one, in the same place in the sheet, so a person who
   has just pressed Done is reading whatever comes back in one spot. Warm rather
   than red: nothing is broken and nothing is lost, and an alarm colour over a
   scan that can simply be run again is an alarm about the wrong thing. */
.problem {
  margin: 2px 0 4px; padding: 12px 13px; border-radius: 12px;
  background: color-mix(in oklab, var(--k-warn) 8%, var(--k-paper));
  border: 1px solid color-mix(in oklab, var(--k-warn) 30%, transparent);
}
.problem .k {
  margin: 0 0 5px; font: 600 14px/1.25 var(--ion-font-family, system-ui); color: var(--k-ink);
}
.problem .saw {
  margin: 0 0 7px; font: 400 12.5px/1.45 var(--ion-font-family, system-ui); color: var(--k-dim);
}
/* The machine's own words, quoted rather than announced: monospace-ish, small, and
   selectable, because the thing people do with this line is read it out. */
.problem .detail {
  margin: 0 0 4px; padding: 8px 10px; border-radius: 9px;
  background: var(--k-paper); border: 1px solid var(--k-line);
  font: 400 11.5px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace;
  color: var(--k-mute); overflow-wrap: anywhere; user-select: text;
}
/* Under the button it belongs to, not above it: it is the instruction for the thing
   they are about to do again. */
.problem .advice {
  margin: 7px 2px 0; font: 400 11.5px/1.4 var(--ion-font-family, system-ui); color: var(--k-mute);
}

.services {
  margin: 0 0 8px; padding: 10px 11px; border-radius: 10px;
  background: var(--k-paper); border: 1px solid color-mix(in oklab, var(--k-accent) 20%, transparent);
}
.lock-lede { margin: 0 0 7px; }
/* The ticks WRAP. Four of them stacked is 130px of a 405px sheet, which pushed the one
   button this card exists for below the fold on a 390-wide phone. */
.ticks { display: flex; flex-wrap: wrap; gap: 0 16px; }
.lockrow {
  display: flex; align-items: center; gap: 8px; padding: 4px 0;
  font: 500 13px/1.3 var(--ion-font-family, system-ui); color: var(--k-ink);
}
.lockrow input { width: 18px; height: 18px; accent-color: var(--k-accent); flex: 0 0 auto; }
.lockrow.island { margin: 0 0 4px; }

/* ── one primary, one quiet, and the rest small ───────────────────────────────── */
.primary {
  margin: 8px 0 0; width: 100%;
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  border: 0; border-radius: 11px; cursor: pointer; padding: 14px; min-height: 50px;
  background: var(--k-ink); color: var(--k-paper);
  font: 600 14.5px/1 var(--ion-font-family, system-ui);
}
.primary:active { opacity: .9; }
.secondary {
  margin: 7px 0 0; width: 100%;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid var(--k-line); border-radius: 11px; cursor: pointer;
  padding: 12px; min-height: 46px;
  background: var(--k-paper); color: var(--k-dim);
  font: 500 13.5px/1 var(--ion-font-family, system-ui);
}
.secondary:active { background: var(--k-fill); }
.tertiary { display: flex; flex-wrap: wrap; gap: 2px 14px; margin: 6px 0 0; }
.tertiary button {
  border: 0; background: none; cursor: pointer; padding: 8px 0; min-height: 36px;
  font: 500 12px/1 var(--ion-font-family, system-ui); color: var(--k-accent);
}

/* The tally reads as a number, because it is the one on this card: bigger than the
   sentence above it and in the ink colour, so an eye coming back after a tap on the
   plan lands on it without hunting. */
.tally { font: 600 14px/1.35 var(--ion-font-family, system-ui); margin: 0 0 2px; }
/* Select all and Clear are the smallest things here on purpose. They are the escape
   hatch from a default that came out wrong, not the way through. */
.picks { gap: 2px 18px; margin: 2px 0 0; }

.wallshot {
  display: flex; align-items: center; gap: 10px; width: 100%; margin: 6px 0 0;
  padding: 10px 11px; min-height: 46px; border: 0; border-radius: 10px;
  background: var(--k-paper); color: var(--k-ink); text-align: left; cursor: pointer;
}
.wallshot:disabled { opacity: .5; }
.wallshot .text { display: grid; gap: 2px; min-width: 0; }
.wallshot .k { margin: 0; font: 600 13px/1.2 var(--ion-font-family, system-ui); }
.wallshot .b { margin: 0; font: 400 11.5px/1.35 var(--ion-font-family, system-ui); color: var(--k-dim); }
</style>
